import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

export interface QueryRule {
    id: string;
    operator: 'AND' | 'OR';
    field: string;
    value: string;
}

export interface FieldOption {
    label: string;
    value: string;
    workspaceName?: string;
    workspaceId?: string;
}

export const BREADCRUMB_BUTTON_STYLE = {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    fontWeight: 'inherit',
    fontSize: 'inherit'
};

export const getAdbDefaultFilters = (navigationState: any) => {
    const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
    const defStartDate = yesterday.clone().subtract(89, 'days').startOf('day');
    return {
        startDate: navigationState?.startDate || defStartDate.format('YYYY-MM-DD'),
        endDate: navigationState?.endDate || yesterday.endOf('day').format('YYYY-MM-DD'),
        env: navigationState?.env || 'both',
        sector: navigationState?.sector || 'all',
        location: navigationState?.location || 'South Central US',
        selectedDateInString: navigationState?.selectedDateInString || '3M',
        yesterday,
        defStartDate
    };
};

export const useAdbBaseState = (navigationState: any, preserveKey: string) => {
    const restoredRef = useRef(false);
    const prevParamsRef = useRef<string>('');
    const defaults = getAdbDefaultFilters(navigationState);

    return {
        restoredRef,
        prevParamsRef,
        PRESERVE_FILTER_KEY: preserveKey,
        ...defaults
    };
};

export const getAdbDateRanges = (yesterday: moment.Moment, stDt?: string) => ({
    'Hrs': yesterday.clone().startOf('day'),
    '7d': yesterday.clone().subtract(6, 'days').startOf('day'),
    '30d': yesterday.clone().subtract(29, 'days').startOf('day'),
    '3M': yesterday.clone().subtract(3, 'months').startOf('day'),
    'custom': stDt ? moment.tz(stDt, 'Asia/Kolkata').startOf('day') : yesterday.clone().startOf('day')
});

export const getNavigationStateHelper = (state: any, overrides = {}) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    env: state.env,
    sector: state.sector,
    location: state.location,
    selectedDateInString: state.selectedDateInString,
    ...overrides
});

export const commonHandleDateSelect = (
    stDt: string,
    selectedDate: string,
    setStartDate: (d: string) => void,
    setEndDate: (d: string) => void,
    setSelectedDateInString: (s: string) => void
) => {
    setSelectedDateInString(selectedDate);
    const yesterday = moment.tz('Asia/Kolkata').subtract(1, 'day');
    const end = yesterday.clone().endOf('day');
    const ranges = getAdbDateRanges(yesterday, stDt);
    const start = ranges[selectedDate as keyof typeof ranges] || moment.tz(stDt, 'Asia/Kolkata').startOf('day');

    setStartDate(start.format('YYYY-MM-DD'));
    setEndDate(end.format('YYYY-MM-DD'));
};

export const syncEnvWithOptions = (
    options: { label: string; value: string }[],
    initialEnvAppliedRef: React.MutableRefObject<boolean>,
    envRef: React.MutableRefObject<string>,
    navigationEnv: string | undefined,
    setEnv: (v: string) => void
) => {
    if (!initialEnvAppliedRef.current && navigationEnv) {
        setEnv(navigationEnv);
        initialEnvAppliedRef.current = true;
    } else {
        const availableValues = options.map(o => o.value);
        const currentEnv = envRef.current;
        if (!availableValues.includes(currentEnv)) {
            const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
            setEnv(fallback);
        }
    }
};

export const commonFetchSectors = async (
    fetchSectorList: (params: any) => Promise<any>,
    setSectorList: (list: string[]) => void
) => {
    const resp = await fetchSectorList({});
    if (resp?.success) {
        setSectorList(resp?.data || []);
    }
};

export const resolveDateSelection = (stDt: string, selectedDate: string = 'Hrs') => {
    let uiSelected = selectedDate;
    let apiKey = uiSelected === 'YTD' ? '3M' : uiSelected;
    const isStartOfYear = stDt && moment(stDt).isSame(moment().startOf('year'), 'day');
    if (uiSelected === 'custom' && isStartOfYear) {
        apiKey = '3M';
        uiSelected = 'YTD';
    }
    return { uiSelected, apiKey };
};

const getInitialDates = (navigationState: any) => {
    const yesterday = moment.tz('Asia/Kolkata').subtract(1, 'day');
    let startDate = yesterday.clone().subtract(3, 'months').startOf('day').format('YYYY-MM-DD');
    let endDate = yesterday.clone().endOf('day').format('YYYY-MM-DD');
    let dateString = '3M';

    if (navigationState?.selectedDateInString && navigationState.selectedDateInString.trim() !== 'custom') {
        startDate = navigationState.startDate || startDate;
        endDate = navigationState.endDate || endDate;
        dateString = navigationState.selectedDateInString;
    }
    return { startDate, endDate, dateString };
};

const applyNavigationDates = (nav: any, preserved: string | null, setters: any) => {
    if (nav.selectedDateInString?.trim() !== 'custom') {
        if (nav.startDate) setters.setStartDate(nav.startDate);
        if (nav.endDate) setters.setEndDate(nav.endDate);
        if (nav.selectedDateInString) setters.setSelectedDateInString(nav.selectedDateInString);
    }
    if (preserved && nav.selectedDateInString === 'customDateRange') {
        const p = JSON.parse(preserved);
        if (p.startDate) setters.setStartDate(p.startDate);
        if (p.endDate) setters.setEndDate(p.endDate);
        if (p.selectedDateInString) setters.setSelectedDateInString(p.selectedDateInString);
        setters.restoredRef.current = true;
    }
};

const applyNavigationFilters = (nav: any, setters: any) => {
    if (nav.env) setters.setEnv(nav.env);
    if (nav.sector) {
        setters.setSector(nav.sector);
        setters.setSectorName(nav.sector);
    }
    if (nav.location) setters.setLocation(nav.location);
    setters.initialEnvAppliedRef.current = false;
};

export const useAdbFilters = (preserveKey: string, initialLocation = 'South Central US') => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const navigationState = routerLocation.state;

    const initials = getInitialDates(navigationState);

    const [startDate, setStartDate] = useState<string>(initials.startDate);
    const [endDate, setEndDate] = useState<string>(initials.endDate);
    const [env, setEnv] = useState(navigationState?.env || 'both');
    const [sector, setSector] = useState(navigationState?.sector || 'all');
    const [sectorName, setSectorName] = useState(navigationState?.sector || 'all');
    const [location, setLocation] = useState(navigationState?.location || initialLocation);
    const [selectedDateInString, setSelectedDateInString] = useState(initials.dateString);
    const [apiDateRangeKey, setApiDateRangeKey] = useState<string>(initials.dateString);

    const envRef = useRef(env);
    const restoredRef = useRef(false);
    const initialEnvAppliedRef = useRef(false);

    useEffect(() => { envRef.current = env; }, [env]);

    const updateHistoryState = useCallback((overrides: any = {}) => {
        const newState = {
            startDate, endDate, env, sector, location, selectedDateInString,
            ...navigationState,
            ...overrides
        };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    }, [navigate, routerLocation.pathname, startDate, endDate, env, sector, location, selectedDateInString, navigationState]);

    const handleDateSelect = useCallback((stDt: string, edDt: string, selectedDate?: string) => {
        const { uiSelected, apiKey } = resolveDateSelection(stDt, selectedDate);
        setSelectedDateInString(uiSelected);
        setApiDateRangeKey(apiKey);
        commonHandleDateSelect(stDt, apiKey, setStartDate, setEndDate, () => { });
    }, []);

    const applyState = useCallback(() => {
        if (!navigationState) return;
        const preservedRaw = sessionStorage.getItem(preserveKey);
        applyNavigationDates(navigationState, preservedRaw, { setStartDate, setEndDate, setSelectedDateInString, restoredRef });
        applyNavigationFilters(navigationState, { setEnv, setSector, setSectorName, setLocation, initialEnvAppliedRef });
    }, [navigationState, preserveKey]);

    useEffect(() => {
        applyState();
    }, [applyState]);

    return {
        startDate, setStartDate,
        endDate, setEndDate,
        env, setEnv,
        sector, setSector,
        sectorName, setSectorName,
        location, setLocation,
        selectedDateInString, setSelectedDateInString,
        apiDateRangeKey, setApiDateRangeKey,
        envRef, restoredRef, initialEnvAppliedRef,
        updateHistoryState, handleDateSelect,
        navigationState
    };
};

export const REGIONS = [
    'ALL',
    'Brazil South',
    'Australia East',
    'UK South',
    'South Central US',
    'East US',
    'Germany West Central'
];

export const REGION_OPTIONS = REGIONS.map(location => ({ label: location, value: location }));

export const ENV_OPTIONS_DEFAULT = [
    { label: 'ALL', value: 'both' },
    { label: 'PROD', value: 'prod' },
    { label: 'NON-PROD', value: 'non-prod' }
];

export const ENV_OPTIONS_EXTENDED = [
    { label: 'ALL', value: 'both' },
    { label: 'PROD', value: 'prod' },
    { label: 'PRE-PROD', value: 'preprod' },
    { label: 'QA', value: 'qa' },
    { label: 'Development', value: 'dev' },
    { label: 'Sandbox', value: 'sandbox' }
];

export function formatCompactNumber(value: number): string {
    const clean = (num: number) => Number.parseFloat(num.toFixed(2)).toString();
    if (value >= 1_000_000) {
        return clean(value / 1_000_000) + 'M';
    }
    if (value >= 1_000) {
        return clean(value / 1_000) + 'K';
    }
    return clean(value);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves a Workspace Name query rule into a Workspace ID filter.
 */
export const resolveWorkspaceFilter = (ruleValue: any, workspaceOptions: FieldOption[]) => {
    const rawValue = ruleValue?.toString() ?? '';
    const valueLower = rawValue.toLowerCase();

    if (valueLower === 'all' || valueLower === '') {
        return { operator: 'AND', field: 'Workspace ID', value: 'All' };
    }

    const opt = workspaceOptions.find(o =>
        o.value === rawValue ||
        (o as any).workspaceId === rawValue ||
        ((o as any).workspaceName && (o as any).workspaceName.toString().toLowerCase() === valueLower) ||
        (o.label && o.label.toString().toLowerCase() === valueLower)
    );

    const finalValue = (opt as any)?.workspaceId || opt?.value || rawValue;
    return { operator: 'AND', field: 'Workspace ID', value: finalValue };
};

/**
 * Maps a single QueryRule to its corresponding API filter object for ADB tables.
 */
export const commonMapRuleToApiFilter = (rule: any, workspaceOptions: FieldOption[]) => {
    const rawValue = rule.value?.toString() ?? '';
    const valueLower = rawValue.toLowerCase();

    if (!rawValue || valueLower === 'all' || valueLower === '') {
        return null;
    }

    if (rule.field === 'Workspace Name' || rule.field === 'Workspace ID') {
        return resolveWorkspaceFilter(rule.value, workspaceOptions);
    }

    if (rule.field === 'Environment' || rule.field === 'Compute Type' || rule.field === 'Status') {
        return { operator: 'OR', field: 'Compute Type', value: rule.value };
    }

    return {
        operator: rule.operator || 'AND',
        field: rule.field,
        value: rule.value
    };
};

// ─── Shared Cluster-Details Column Helpers ───────────────────────────────────

export const CLUSTER_DETAILS_DEFAULT_COLUMNS = [
    'Workspace ID',
    'Workspace Name',
    'Environment',
    'Compute Type',
    'Cluster Name',
    'Usage Date',
    'DBU',
    'Compute Cost',
    'Total Runtime Hours',
];

export const CLUSTER_COMPUTE_TYPE_OPTIONS = [
    { label: 'All', value: 'All' },
    { label: 'INTERACTIVE', value: 'INTERACTIVE' },
    { label: 'JOB', value: 'JOB' },
    { label: 'SQL', value: 'SQL' },
];

export const CLUSTER_AUTO_TERMINATION_OPTIONS = [
    { label: 'Null', value: 'Null' },
    { label: 'All', value: 'All' },
];

/** Returns the column definitions shared by all cluster-details tables. */
export function getClusterDetailsColumns(): any[] {
    return [
        {
            key: 'workspace_id',
            name: 'Workspace ID',
            formatter({ row }: any) {
                return React.createElement('div', { title: row?.workspace_id }, row?.workspace_id || '--');
            },
            minWidth: 180,
        },
        {
            key: 'workspace_name',
            name: 'Workspace Name',
            formatter({ row }: any) {
                return React.createElement('div', { title: row?.workspace_name }, row?.workspace_name || '--');
            },
            minWidth: 200,
        },
        {
            key: 'env',
            name: 'Environment',
            formatter({ row }: any) {
                return React.createElement('div', {}, row?.env?.toUpperCase() || '--');
            },
            minWidth: 120,
        },
        {
            key: 'computetype',
            name: 'Compute Type',
            formatter({ row }: any) {
                return React.createElement('div', {}, row?.computetype || '--');
            },
            minWidth: 140,
        },
        {
            key: 'cluster_name',
            name: 'Cluster Name',
            formatter({ row }: any) {
                return React.createElement('div', { title: row?.cluster_name }, row?.cluster_name || '--');
            },
            minWidth: 220,
        },
        {
            key: 'usage_date',
            name: 'Usage Date',
            formatter({ row }: any) {
                const d = row?.usage_date;
                return React.createElement('div', {}, d ? moment.utc(d).format('YYYY-MM-DD') : '--');
            },
            minWidth: 140,
        },
        {
            key: 'dbu',
            name: 'DBU',
            formatter({ row }: any) {
                const v = row?.dbu;
                return React.createElement('div', {}, typeof v === 'number' ? formatCompactNumber(v) : v || '--');
            },
            minWidth: 120,
            align: 'right',
        },
        {
            key: 'compute_cost',
            name: 'Compute Cost',
            formatter({ row }: any) {
                const v = row?.compute_cost;
                return React.createElement('div', {}, typeof v === 'number' ? `$${v.toFixed(2)}` : v || '--');
            },
            minWidth: 140,
            align: 'right',
        },
        {
            key: 'total_runtime_hours',
            name: 'Total Runtime Hours',
            formatter({ row }: any) {
                const v = row?.total_runtime_hours;
                return React.createElement('div', {}, typeof v === 'number' ? v.toFixed(2) : v || '--');
            },
            minWidth: 160,
            align: 'right',
        },
        {
            key: 'job_name',
            name: 'Job Name',
            formatter({ row }: any) {
                return React.createElement('div', { title: row?.job_name }, row?.job_name || '--');
            },
            minWidth: 180,
        },
        {
            key: 'cluster_id',
            name: 'Cluster ID',
            formatter({ row }: any) {
                return React.createElement('div', { title: row?.cluster_id }, row?.cluster_id || '--');
            },
            minWidth: 180,
        },
        {
            key: 'auto_termination_minutes',
            name: 'Auto Termination (min)',
            formatter({ row }: any) {
                const v = row?.auto_termination_minutes;
                return React.createElement('div', {}, v ?? '--');
            },
            minWidth: 180,
            align: 'left',
        },
    ];
}

/** Builds the API sort-by string from a sortingInfo object and column list. */
export function buildApiSortBy(
    sortingInfo: { sortBy: string; sortDir: string },
    cols: any[]
): string | undefined {
    if (!sortingInfo?.sortBy) return undefined;
    const keyToNameMap: Record<string, string> = {};
    cols.forEach((c: any) => {
        if (c?.key) keyToNameMap[String(c.key)] = c.name || String(c.key);
        if (c?.name) keyToNameMap[String(c.name)] = c.name;
    });
    const fallbackMap: Record<string, string> = { dbu: 'DBU', compute_cost: 'Compute Cost' };
    const key = String(sortingInfo.sortBy);
    return keyToNameMap[key] || fallbackMap[key] || undefined;
}

// ─────────────────────────────────────────────────────────────────────────────

export const SECTOR_OPTIONS_LIST = [
    'latam', 'apac', 'TBD', 'amesa', 'essa', 'flna', 'global', 'tbd', 'pbna', 'pfna', 'snt'
];

export const unwrap = (node: any) => {
    if (!node || typeof node !== 'object') return node;
    const keys = Object.keys(node);
    return (keys.length === 1 && node[keys[0]] && typeof node[keys[0]] === 'object')
        ? node[keys[0]]
        : node;
};

export const resolveCost = (src: any) => {
    if (!src || typeof src !== 'object') return 0;
    const keys = [
        'count', 'cost', 'totalDBU', 'dbu', 'total_runtime_hours', 'runTimeHours', 'usage_quantity',
        'total_count', 'total_cost', 'total_cluster_cost', 'total_runtime_seconds', 'total_runtime_minutes',
        'distinctWarehouseIDCount', 'distinctStatementIDCount', 'distinctQueryIDCount', 'distinctUserIDCount',
        'totalCount', 'totalCost', 'value', 'sqlWarehouseCost', 'sqlWarehouseDbu',
        'catalogCost', 'dollarsEstimated', 'dbuEstimated', 'costcompute', 'totalComputeCost'
    ];
    for (const k of keys) {
        if (src[k] !== undefined && src[k] !== null) {
            const val = Number(src[k]);
            if (!Number.isNaN(val)) return val;
        }
    }
    if (src.costObj !== undefined && src.costObj !== null) {
        const val = Number(src.costObj);
        if (!Number.isNaN(val)) return val;
    }
    return 0;
};

export const handleExportExcel = (resp: any) => {
    if (resp?.success && resp?.data) {
        const { buffer, filename, contentType } = resp.data;
        if (buffer?.data) {
            const byteArray = new Uint8Array(buffer.data);
            const blob = new Blob([byteArray], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        }
    }
};

export const commonGenerateColumns = (mapping: Record<string, string>, options: any = {}) => {
    return Object.entries(mapping).map(([key, displayName]) => ({
        key,
        name: displayName,
        sortable: options.sortable ?? true,
        resizable: options.resizable ?? true,
        width: options.width ?? (options.isAutoWidth ? undefined : 200),
        minWidth: options.minWidth ?? 150,
        ...options.overrides?.[key]
    }));
};

export const useAdbAnalysisTable = (fetchService: any, filters: any, filterFn: (data: any[]) => any[], extraParams: any = {}) => {
    const [tableData, setTableData] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [loader, setLoader] = useState(false);

    const callFetchService = useCallback((pageVal: number, pageSizeVal: number, exportType?: string) => {
        const { sortBy, sortDir } = sortingInfo;
        if (typeof fetchService === 'function' && fetchService.length === 1) {
            return fetchService({
                startDate: filters.startDate,
                endDate: filters.endDate,
                env: filters.env,
                sector: filters.sector,
                location: filters.location,
                selectedDateInString: filters.selectedDateInString,
                page: pageVal,
                limit: pageSizeVal,
                sortField: sortBy,
                sortOrder: sortDir,
                exportType,
                ...extraParams
            });
        }
        return fetchService(
            filters.startDate,
            filters.endDate,
            filters.env,
            filters.sector,
            filters.location,
            filters.selectedDateInString,
            pageVal,
            pageSizeVal,
            ...Object.values(extraParams),
            sortBy,
            sortDir,
            exportType
        );
    }, [fetchService, filters, extraParams, sortingInfo]);

    const fetchData = useCallback(async () => {
        setLoader(true);
        const resp = await callFetchService(page, pageSize);
        if (resp?.success) {
            const docs = resp.data?.docs ?? [];
            setTableData(filterFn(docs));
            setPage(resp.data.page);
            setPageSize(resp.data.limit);
            setPaginationData({
                totalElements: resp.data.totalDocs,
                totalPages: resp.data.totalPages
            });
        }
        setLoader(false);
    }, [callFetchService, page, pageSize, filterFn]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePageChange = (_event: any, value: number) => setPage(value);
    const handlePageSizeChange = (event: any) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };
    const handleSorting = (info: any) => setSortingInfo(info);

    const handleExport = async () => {
        const resp = await callFetchService(page, pageSize, 'excel');
        handleExportExcel(resp);
    };

    return {
        tableData, setTableData,
        columns, setColumns,
        page, setPage,
        pageSize, setPageSize,
        sortingInfo, setSortingInfo,
        paginationData, setPaginationData,
        loader, setLoader,
        handlePageChange,
        handlePageSizeChange,
        handleSorting,
        handleExport
    };
};

/**
 * 🌲 Hierarchy / Tree Table Utilities
 */

export const resolveTreeNodeCount = (node: any) => {
    if (!node) return 0;
    return node.total_count ?? node.total_cluster_cost ?? node.dbu ?? node.distinctJobRunIDCount ?? node.distinctRunIDCount ?? node.compute_count ?? node.compute_cost ?? node.cost ?? 0;
};

export function commonFlattenTimeSeries(data: any[]): any[] {
    const rows: any[] = [];
    const pushRow = (id: string, label: string, count: number, children: any[], level = 0, date?: string) => {
        rows.push({
            id,
            time_range: label,
            count: count || 0,
            children: children || [],
            isExpanded: false,
            level,
            date
        });
    };

    data.forEach((wrapper: any, idx: number) => {
        const key = Object.keys(wrapper)[0];
        const item = wrapper[key];
        if (!item) return;

        const label = item.year_name || item.month_name || item.week_name || item.day_name || `Row ${idx}`;
        const id = `${key}-${idx}-${label}`;
        pushRow(id, label, resolveTreeNodeCount(item), item.children || [], 0, item.date);
    });

    return rows;
}

export function commonGetImmediateChildrenForRow(row: any): any[] {
    const childrenRows: any[] = [];
    if (!Array.isArray(row.children)) return childrenRows;

    row.children.forEach((obj: any, idx: number) => {
        const key = Object.keys(obj)[0];
        const item = obj[key];
        if (!item) return;

        const label = item.year_name || item.month_name || item.week_name || item.day_name || item.date || `Child ${idx}`;
        const id = `${row.id}-${key}-${idx}-${label}`;

        childrenRows.push({
            id,
            time_range: label,
            count: resolveTreeNodeCount(item),
            children: item.children || [],
            isExpanded: false,
            level: row.level + 1,
            date: item.date
        });
    });

    return childrenRows;
}

export function commonExpandRow(row: any, rows: any[], parentIdx: number) {
    const childrenRows = commonGetImmediateChildrenForRow(row);
    const newRows = [...rows];
    newRows.splice(parentIdx + 1, 0, ...childrenRows);
    newRows[parentIdx] = { ...row, isExpanded: true };
    return newRows;
}

export function commonCollapseDescendants(row: any, rows: any[], parentIdx: number) {
    let removeCount = 0;
    for (let i = parentIdx + 1; i < rows.length; i++) {
        if (rows[i].level > row.level) removeCount++;
        else break;
    }
    const newRows = [...rows];
    newRows.splice(parentIdx + 1, removeCount);
    newRows[parentIdx] = { ...row, isExpanded: false };
    return newRows;
}

export const useAdbHierarchyTable = (apiData: any[], pageSize: number) => {
    const [tableData, setTableData] = useState<any[]>([]);
    const rowsRef = useRef<any[]>([]);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });

    useEffect(() => {
        if (!apiData) return;
        const flat = commonFlattenTimeSeries(apiData);
        rowsRef.current = flat;
        setTableData(flat);
        setPaginationData({
            totalElements: flat.length,
            totalPages: Math.ceil(flat.length / pageSize)
        });
    }, [apiData, pageSize]);

    const toggleExpandRow = useCallback((rowId: string) => {
        const rows = rowsRef.current;
        const idx = rows.findIndex((r: any) => r.id === rowId);
        if (idx === -1) return;
        const row = rows[idx];
        let newRows;
        if (row.isExpanded) newRows = commonCollapseDescendants(row, rows, idx);
        else newRows = commonExpandRow(row, rows, idx);
        rowsRef.current = newRows;
        setTableData(newRows);
    }, []);

    return { tableData, setTableData, toggleExpandRow, paginationData, rowsRef };
};
